import { faEye, faEyeSlash, faPen, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, Tabs, Tab, TextInput, Loading, Button as WfpButton } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"
import Select from "react-select"
import {
  AIMS_MODULE,
  GET_ITEM_LIMIT,
  GORP_MODULE,
  HH_MODULE,
  MARKET_MODULE,
  MFI_MODULE,
  RPME_MODULE,
} from "constants/constants"
import { useAppSelector } from "hooks/hooksStore"
import useCommon from "hooks/useCommon"
import { AccessLog, ApiKey, ApiKeyList } from "interfaces/dataInterfaces"
import { ReactElement, useEffect, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import {
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useEditApiKeyMutation,
  useGetApiKeyQuery,
  useGetApiKeysQuery,
  useGetDataProviderPlatformsQuery,
  useGetVisibilityLevelQuery,
} from "services/core/user"
import { useGetAccessLogsQuery } from "services/core/accessLog"
import { RootState } from "store/store"
import { formatDate } from "utils/helpers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import DataBridgesApiKeysTab from "components/Users/DataBridgesApiKeysTab"

const defaultFieldsVals: ApiKey = { dataProviderPlatformId: 0, keyValue: "", name: "" }

function UserProfilePage() {
  const { showSnackBar } = useCommon()
  const { authUser } = useAppSelector((state: RootState) => state.common)

  const [hideKey, setHideKey] = useState(true)
  const [authPageNumber, setAuthPageNumber] = useState(1)
  const [getAuthItemLimit, setGetAuthItemLimit] = useState(GET_ITEM_LIMIT)
  const [apiPageNumber, setApiPageNumber] = useState(1)
  const [getApiItemLimit, setGetApiItemLimit] = useState(GET_ITEM_LIMIT)
  const [fields, setFields] = useState({ ...defaultFieldsVals })
  const [actionItem, setActionItem] = useState<{
    item: any
    action: "delete" | "edit" | "addNew"
  } | null>(null)

  const { data: apiKeys } = useGetApiKeysQuery({
    skip: apiPageNumber === 1 ? 0 : (apiPageNumber - 1) * getApiItemLimit,
    take: getApiItemLimit,
  })
  const { data: apiKey, isFetching: fetchingApiKey } = useGetApiKeyQuery(actionItem?.item?.dataProviderKeyId, {
    skip: !actionItem?.item?.dataProviderKeyId,
  })
  const { data: accessLogs } = useGetAccessLogsQuery({
    skip: authPageNumber === 1 ? 0 : (authPageNumber - 1) * getAuthItemLimit,
    take: getAuthItemLimit,
  })
  const { data: visibilityRes } = useGetVisibilityLevelQuery(
    {
      userId: authUser?.applicationUserId || "",
    },
    { skip: !authUser?.applicationUserId },
  )
  const { data: platforms, isFetching: platformsLoading } = useGetDataProviderPlatformsQuery()
  const [createApiKey] = useCreateApiKeyMutation()
  const [deleteApiKey, { isLoading: isDeletingApiKey }] = useDeleteApiKeyMutation()
  const [editApiKey] = useEditApiKeyMutation()

  const handleKeyForm = async () => {
    try {
      if (!fields.dataProviderPlatformId || !fields.keyValue || !fields.name) {
        showSnackBar("All fields are required!", "error")
        return
      }

      if (actionItem?.action === "edit")
        await editApiKey({ ...actionItem.item, ...fields, dataProviderPlatformName: undefined }).unwrap()
      else await createApiKey(fields).unwrap()
      showSnackBar(`Api key ${actionItem?.action === "edit" ? "updated" : "added"} successfully!`)
      setActionItem(null)
      setFields({ ...defaultFieldsVals })
    } catch (error: any) {
      showSnackBar(error.data || error.message, error.originalStatus === 200 ? "success" : "error")
      if (error.originalStatus === 200) {
        setActionItem(null)
        setFields({ ...defaultFieldsVals })
      }
    }
    if (!hideKey) setHideKey(true)
  }

  const handleKeyDelete = async (dataProviderKeyId: number) => {
    try {
      await deleteApiKey(dataProviderKeyId).unwrap()
      showSnackBar("Api key deleted successfully!")
    } catch (error: any) {
      showSnackBar(error.data || error.message, error.originalStatus === 200 ? "success" : "error")
    } finally {
      setActionItem(null)
    }
  }

  useEffect(() => {
    if (!apiKey) return
    if (actionItem?.item?.dataProviderKeyId) setFields({ ...apiKey })
  }, [apiKey, actionItem])

  const authCols: Array<ColumnShape<AccessLog>> = [
    {
      key: "date",
      dataKey: "loginDate",
      title: "Date",
      width: 75,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => formatDate(rowData.loginDate, "DD/MM/YYYY HH:mm:ss"),
    },
    {
      key: "ipAddress",
      dataKey: "userIp",
      title: "IP Address",
      width: 95,
      flexGrow: 1,
    },
    {
      key: "device",
      dataKey: "userAgent",
      title: "Device",
      width: 130,
      flexGrow: 4,
    },
  ]

  const apiCols: Array<ColumnShape<ApiKeyList>> = [
    {
      key: "platform",
      dataKey: "dataProviderPlatformName",
      title: "Platform",
      width: 180,
    },
    {
      key: "keyName",
      dataKey: "name",
      title: "Key Name",
      width: 180,
      flexGrow: 2,
    },
    {
      key: "key",
      dataKey: "keyValue",
      title: "Key",
      width: 180,
      flexGrow: 1,
    },
    {
      key: "updateDate",
      dataKey: "updateDate",
      title: "Last Update",
      width: 180,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => formatDate(rowData.updateDate, "DD/MM/YYYY HH:mm:ss"),
    },
    {
      key: "action",
      width: 100,
      align: Column.Alignment.RIGHT,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex justify-end gap-x-1">
          <FontAwesomeIcon
            icon={faPen}
            className="p-1 rounded text-[var(--interactive-01)] hover:text-[var(--interactive-02)] transition-colors cursor-pointer"
            onClick={() => setActionItem({ item: rowData, action: "edit" })}
            title="Edit"
          />
          <FontAwesomeIcon
            icon={faTrash}
            title="Delete"
            className="p-1 rounded text-[var(--support-01)] hover:text-red-800 transition-colors cursor-pointer"
            onClick={() => setActionItem({ item: rowData, action: "delete" })}
          />
        </div>
      ),
    },
  ]

  const RenderRole = ({ module }: { module: number }): ReactElement => (
    <p className="mt-1">
      {authUser?.roles.find(r => r.applicationModuleId === module)?.roleName ?? (
        <span className="text-gray-400">No Access</span>
      )}
    </p>
  )

  return (
    <div>
      <Tabs disableAnimation>
        <Tab label="Access and permissions" href="" role="tab" selected tabIndex={1}>
          <div>
            <div className="grid grid-cols-5 ">
              <div className="col-span-1">
                <p className="mt-1">User email:</p>
                <p className="mt-1">Visibility level:</p>
              </div>

              <div className="col-span-4">
                <p className="mt-1">{authUser?.nameid}</p>
                <p className="mt-1">{visibilityRes?.visibility}</p>
              </div>
            </div>

            <p className="mt-3 mb-1">
              <b>Roles and permissions</b>
            </p>

            <div className="grid grid-cols-5 ">
              <div className="col-span-1">
                <p className="mt-1">Market price:</p>
                <p className="mt-1">MFI:</p>
                <p className="mt-1">RPME:</p>
                <p className="mt-1">AIMS:</p>
                <p className="mt-1">GORP:</p>
                <p className="mt-1">Household:</p>
              </div>

              <div className="col-span-4">
                <RenderRole module={MARKET_MODULE} />
                <RenderRole module={MFI_MODULE} />
                <RenderRole module={RPME_MODULE} />
                <RenderRole module={AIMS_MODULE} />
                <RenderRole module={GORP_MODULE} />
                <RenderRole module={HH_MODULE} />
              </div>
            </div>

            <p className="mt-3 mb-3">
              <b>Latest authentications</b>
            </p>

            <div style={{ height: "100%", minHeight: "50vh", marginTop: "10px" }}>
              <AutoResizer>
                {({ width, height }) => (
                  <BaseTable
                    data={accessLogs?.items || []}
                    width={width}
                    height={height}
                    rowKey="loginDate"
                    className="custom-table"
                    rowHeight={38}
                    headerHeight={45}
                    columns={authCols}
                    emptyRenderer={() => <p>No records to display</p>}
                  />
                )}
              </AutoResizer>
            </div>

            <Pagination
              totalItems={accessLogs?.count}
              pageSize={getAuthItemLimit}
              pageSizes={[10, 20, 30]}
              page={authPageNumber}
              onChange={(event: any) => {
                setAuthPageNumber((event as any).page)
                setGetAuthItemLimit((event as any).pageSize)
              }}
            />
          </div>
        </Tab>

        <Tab label="External API Keys" href="" role="tab" selected={false} tabIndex={2}>
          <div>
            <div className="d-flex justify-end w-full">
              <WfpButton
                kind="primary"
                onClick={() => setActionItem({ action: "addNew", item: {} })}
                iconDescription="Add New Key"
                icon={iconAddOutline}
                iconReverse
              >
                Add New API Key
              </WfpButton>
            </div>

            <div style={{ height: "calc(100vh - 280px)", minHeight: "60vh", marginTop: "10px" }}>
              <AutoResizer>
                {({ width, height }) => (
                  <BaseTable
                    data={apiKeys?.items || []}
                    width={width}
                    height={height}
                    rowKey="dataProviderKeyId"
                    className="custom-table"
                    rowHeight={38}
                    headerHeight={45}
                    columns={apiCols}
                    emptyRenderer={() => <p>No records to display</p>}
                  />
                )}
              </AutoResizer>
            </div>

            <Pagination
              totalItems={apiKeys?.count}
              pageSize={getApiItemLimit}
              pageSizes={[10, 20, 30]}
              page={apiPageNumber}
              onChange={(event: any) => {
                setApiPageNumber((event as any).page)
                setGetApiItemLimit((event as any).pageSize)
              }}
            />
          </div>
        </Tab>

        <Tab label="Databridges API Keys" href="" role="tab" selected={false} tabIndex={3}>
          <DataBridgesApiKeysTab />
        </Tab>
      </Tabs>

      <Modal
        open={actionItem?.action === "edit" || actionItem?.action === "addNew"}
        modalHeading={actionItem?.action === "edit" ? "Edit key" : "Add new key"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleKeyForm}
        onRequestClose={() => {
          setActionItem(null)
          setFields({ ...defaultFieldsVals })
          if (!hideKey) setHideKey(true)
        }}
      >
        {fetchingApiKey ? (
          <div className="d-flex justify-center">
            <Loading small withOverlay={false} />
          </div>
        ) : (
          <form onSubmit={handleKeyForm}>
            <div className="wfp--form-item">
              <label className="wfp--label">Platform</label>
              <Select
                isSearchable
                isLoading={platformsLoading}
                placeholder="Select Platform"
                className="dropdown w-96"
                key={actionItem?.item.dataProviderPlatformId}
                classNamePrefix="select"
                isDisabled={actionItem?.action === "edit"}
                value={platforms?.find(p => Number(p.value) === Number(fields.dataProviderPlatformId)) ?? null}
                getOptionLabel={opt => opt.name}
                options={platforms || []}
                onChange={(e: any) => setFields({ ...fields, dataProviderPlatformId: Number(e.value) })}
              />
            </div>
            <div className="mt-2">
              <TextInput
                labelText="Name"
                placeholder="Enter key name here..."
                value={fields.name}
                onChange={e => setFields({ ...fields, name: e.target.value })}
              />
            </div>
            <div className="mt-2 d-flex align-center justify-between">
              <TextInput
                // @ts-ignore
                type={hideKey ? "password" : "text"}
                labelText="Key"
                placeholder="Enter key here..."
                value={fields.keyValue}
                onChange={e => setFields({ ...fields, keyValue: e.target.value })}
                className="mr-1"
              />
              <FontAwesomeIcon
                icon={hideKey ? faEye : faEyeSlash}
                color="var(--interactive-01)"
                className="mt-2 cursor-pointer"
                size="sm"
                width="20px"
                onClick={() => setHideKey(prev => !prev)}
              />
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={actionItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm delete"
        primaryButtonText="Delete"
        primaryButtonDisabled={isDeletingApiKey}
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleKeyDelete(actionItem?.item.dataProviderKeyId as number)}
        onRequestClose={() => setActionItem(null)}
      >
        {`Are you sure you want to delete API key "${actionItem?.item?.name || ""}"?`}
      </Modal>
    </div>
  )
}

export default UserProfilePage
